import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';

import type { MinimalTarget } from '@/generated/model';
import MobileHeader from '@/shared/ui/mobile-header/MobileHeader';
import { TabValue } from '@/types';
import { datePeriodMD } from '@/utilities/dateUtils';

import type { RefuelsDateRange } from '../../types/RefuelsDateRange.types';
import { RefuelDateTabLabel } from './RefuelDateTabLabel';
import { RefuelHeaderTitle } from './RefuelHeaderTitle';
import { RefuelTargetTabIcon } from './RefuelTargetTabIcon';

type RefuelHeaderProps = {
  tab: TabValue;
  selectedTarget: MinimalTarget | undefined;
  selectedDates: RefuelsDateRange | undefined;
  canGoForward: boolean;
  onTabChange: (tab: TabValue) => void;
};

export function RefuelHeader({ tab, selectedTarget, selectedDates, canGoForward, onTabChange }: RefuelHeaderProps) {
  const { t } = useTranslation();

  const isResultTab = tab === TabValue.result;
  const intervalLength = selectedDates ? datePeriodMD(selectedDates.from, selectedDates.to, t) : '';
  const dateLabel = intervalLength || t('refuels.period');

  function navigateBack() {
    onTabChange(TabValue.dateSelect);
  }

  function navigateForward() {
    onTabChange(TabValue.result);
  }

  function handleTabChange(_event: SyntheticEvent, value: TabValue) {
    onTabChange(value);
  }

  return (
    <MobileHeader
      title={
        <RefuelHeaderTitle
          selectedTarget={selectedTarget}
          showTarget={isResultTab}
          fallbackTitle={t('mainMenu.refuels')}
        />
      }
      onBack={isResultTab ? navigateBack : undefined}
      onForward={canGoForward ? navigateForward : undefined}
    >
      {!isResultTab && (
        <Tabs
          variant="fullWidth"
          value={tab}
          indicatorColor="secondary"
          textColor="secondary"
          onChange={handleTabChange}
        >
          <Tab
            data-qa-element-id="target_selector_tab"
            value={TabValue.targetSelect}
            icon={<RefuelTargetTabIcon isSelected={selectedTarget !== undefined} />}
            label={selectedTarget?.name ?? t('refuels.selectVehicle')}
            sx={{ textTransform: 'none' }}
          />
          <Tab
            data-qa-element-id="date_selector_tab"
            value={TabValue.dateSelect}
            icon={<CalendarMonth />}
            label={<RefuelDateTabLabel selectedDates={selectedDates} label={dateLabel} />}
            sx={{ opacity: !selectedTarget ? 0 : 1, textTransform: 'none' }}
            disabled={!selectedTarget}
          />
        </Tabs>
      )}
    </MobileHeader>
  );
}
